import { BehaviorSubject } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { Room } from './room';
import { RoomStatus } from 'src/app/shared/types/room-status';
import { Countdown } from 'src/app/shared/utils/app.utils';

export class RoomTimer {
  private room: Room | undefined;
  private timer!: Countdown;
  private messageSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  message$ = this.messageSubject.asObservable();

  constructor(private translocoService: TranslocoService) {}

  updateRoom(room: Room) {
    this.room = room;
    this.resetTimer();
  }

  resetTimer(): void {
    if (this.room === undefined) {
      return;
    }

    const status = this.room.status();

    this.timer?.stop();
    if (status !== RoomStatus.Processing) {
      this.timer = new Countdown();
      this.timer.start(this.room?.deadline(), () => {
        if (this.room) {
          const now = Date.now();
          const secondsLeft = Math.round((this.room?.deadline() - now) / 1000);
          this.updateCountdown(secondsLeft);
        }
      });
    } else {
      this.updateCountdown(0);
    }
  }

  private updateCountdown(secondsLeft: number): void {
    if (this.room === undefined) {
      return;
    }

    if (this.translocoService.getActiveLang() === undefined) {
      return;
    }

    switch (this.room.status()) {
      case RoomStatus.Cancelled:
      case RoomStatus.Draw:
      case RoomStatus.Lost:
      case RoomStatus.Won:
        this.messageSubject.next(Countdown.asDate(this.room?.deadline()));
        break;
      case RoomStatus.Open:
        this.messageSubject.next(
          this.translocoService.translate('components.timeout-label.status-open', {
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
        break;
      case RoomStatus.Start:
        this.messageSubject.next(
          this.translocoService.translate('components.timeout-label.status-start', {
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
        break;
      case RoomStatus.Finish:
        this.messageSubject.next(
          this.translocoService.translate('components.timeout-label.status-finish', {
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
        break;
      case RoomStatus.OpponentToStart:
      case RoomStatus.OpponentToFinish:
        this.messageSubject.next(
          this.translocoService.translate('components.timeout-label.status-opponent', {
            opponent: this.room.opponent(),
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
        break;
      case RoomStatus.Processing:
        this.messageSubject.next(
          this.translocoService.translate('components.timeout-label.status-processing'),
        );
        break;
    }
  }
}
